function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t["return"] && (u = t["return"](), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Chart as ChartJS, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend } from 'chart.js';
import { Radar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SCALES_BY_ID } from "../../constants";
import { MOTIVATOR_COLORS } from "../../colors";
import GapTooltips from "./GapTooltips";
import CustomPointLabels from "./CustomPointLabels";
var FADED_MID_BLUE = 'rgba(157, 195, 204, 0.4)';
var LIGHT_BLUE = 'rgba(112, 155, 175)';
var LIGHT_RED = 'rgba(208, 111, 110)';
var FADED_LIGHT_RED = 'rgba(112, 155, 175, 0.46)';
var FADED_LIGHT_BLUE = 'rgba(208, 111, 110, 0.63)';
var DATASET_COLOR_IDENTIFIERS = [FADED_LIGHT_RED, FADED_LIGHT_BLUE];
ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend, ChartDataLabels);
var getGaps = function getGaps(datasets) {
  var dataset1 = datasets[0];
  var dataset2 = datasets[1];
  var result = [];
  for (var i = 0; i < dataset1.length; i++) {
    // Criteria for GAPs
    if (parseInt(dataset1[i]._model.lines[0], 10) <= 30 && parseInt(dataset2[i]._model.lines[0], 10) >= 70 || parseInt(dataset1[i]._model.lines[0], 10) >= 70 && parseInt(dataset2[i]._model.lines[0], 10) <= 30) {
      result.push({
        first: dataset1[i],
        second: dataset2[i],
        idx: i
      });
    }
  }
  return result;
};
var getTickLabels = function getTickLabels(val, t) {
  switch (val) {
    case 10:
      return t('neutral');
    case 30:
      return t('nice_to_have');
    case 70:
      return t('need_to_have');
    default:
      return '';
  }
};
var getTickLineColor = function getTickLineColor(idx) {
  switch (idx) {
    case 0:
      return FADED_MID_BLUE;
    case 2:
      return FADED_MID_BLUE;
    case 6:
      return FADED_MID_BLUE;
    case 9:
      return FADED_MID_BLUE;
    default:
      return 'transparent';
  }
};
var getMidPoint = function getMidPoint(pos1, pos2) {
  var midPointX = (pos1.x + pos2.x) / 2;
  var midPointY = (pos1.y + pos2.y) / 2;
  return {
    x: midPointX,
    y: midPointY
  };
};
var RadarChartJS = function RadarChartJS(_ref) {
  var data = _ref.data,
    onSelectMotivator = _ref.onSelectMotivator,
    selectedGap = _ref.selectedGap,
    onMixpanelEvent = _ref.onMixpanelEvent;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var theme = useTheme();
  var matches = useMediaQuery(theme.breakpoints.down('sm'));
  var _useState = useState(matches ? false : true),
    _useState2 = _slicedToArray(_useState, 2),
    responsiveOption = _useState2[0],
    setResponsiveOption = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    selected = _useState4[0],
    setSelected = _useState4[1];
  var _useState5 = useState(true),
    _useState6 = _slicedToArray(_useState5, 2),
    onLoad = _useState6[0],
    setOnLoad = _useState6[1];
  var _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    motivatorIconPositions = _useState8[0],
    setMotivatorIconPositions = _useState8[1];
  var _useState9 = useState(null),
    _useState10 = _slicedToArray(_useState9, 2),
    gapTooltips = _useState10[0],
    setGapTooltips = _useState10[1];
  var _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    pointTooltipHovered = _useState12[0],
    setPointTooltipHovered = _useState12[1];
  var chartRef = useRef();
  var iconPositionsSet = useRef();
  var selectedRef = useRef();
  var gapTooltipsRef = useRef();
  var handleSetSelected = function handleSetSelected(selectedMotivator) {
    setOnLoad(false);
    if ((selectedMotivator === null || selectedMotivator === void 0 ? void 0 : selectedMotivator.scaleId) === (selected === null || selected === void 0 ? void 0 : selected.scaleId)) {
      if (onSelectMotivator) {
        onSelectMotivator(null);
      }
      // Remove selected if you click the same motivator icon again
      setSelected(null);
      selectedRef.current = null;
    } else {
      if (onSelectMotivator) {
        onSelectMotivator(selectedMotivator);
      }
      setSelected(selectedMotivator);
      selectedRef.current = selectedMotivator;
    }
  };
  var handleMixpanelEvent = function handleMixpanelEvent(mixpanel_event) {
    if (onMixpanelEvent) {
      if (!pointTooltipHovered) onMixpanelEvent(mixpanel_event);
    }
  };
  var handleSetMotivatorIconPositions = function handleSetMotivatorIconPositions(positions) {
    if (!iconPositionsSet.current) {
      setMotivatorIconPositions(positions);
      iconPositionsSet.current = true;
      for (var key in positions) {
        if (positions[key].isSelectable) {
          setOnLoad(true);
          var selectedMotivator = {
            scaleId: positions[key].scaleId,
            idx: positions[key].idx,
            gap: positions[key].metadata.gap
          };
          setSelected(selectedMotivator);
          if (onSelectMotivator) {
            onSelectMotivator(selectedMotivator);
          }
          break;
        }
      }
    }
  };
  var handleSetTooltips = function handleSetTooltips(tooltips) {
    if (!gapTooltipsRef.current) {
      gapTooltipsRef.current = true;
      setGapTooltips(tooltips);
    }
  };
  var reset = function reset() {
    iconPositionsSet.current = null;
    gapTooltipsRef.current = null;
    selectedRef.current = null;
    setSelected(null);
    setGapTooltips(null);
    setMotivatorIconPositions(null);
  };
  useEffect(function () {
    reset();
  }, [data]);
  useEffect(function () {
    // Reset motivator icon and gap tooltip positions whenever the screen resizes or refocuses
    // Add 500 ms delay to allow redraw and setting icon positions after resizing has finished
    var detectWindowEvents = function detectWindowEvents() {
      var redraw = function redraw() {
        iconPositionsSet.current = null;
        gapTooltipsRef.current = null;
        if (chartRef) {
          chartRef.update();
        }
      };
      var doit;
      window.addEventListener('resize', function () {
        setResponsiveOption(matches ? false : true);
        clearTimeout(doit);
        doit = setTimeout(redraw, 500);
      });
      window.addEventListener('focus', function () {
        iconPositionsSet.current = null;
        gapTooltipsRef.current = null;
      });
    };
    detectWindowEvents();
  }, []);
  useEffect(function () {
    setSelected(selectedGap);
    if (onSelectMotivator) {
      onSelectMotivator(selectedGap);
    }
    selectedRef.current = selectedGap;
    if (onLoad) {
      setOnLoad(false);
    }
  }, [selectedGap]);
  useEffect(function () {
    // Reset gap tooltips whenever a different motivator is selected
    gapTooltipsRef.current = false;
  }, [selected]);
  var chartData = {
    labels: Object.keys(SCALES_BY_ID).map(function (scaleId) {
      return t(SCALES_BY_ID[scaleId].replace(' ', '_')).toUpperCase();
    }),
    datasets: data.map(function (respondent, idx) {
      return {
        label: respondent.label,
        data: respondent.data,
        backgroundColor: selected && !onLoad ? 'transparent' : DATASET_COLOR_IDENTIFIERS[idx],
        borderColor: selected && !onLoad ? 'transparent' : DATASET_COLOR_IDENTIFIERS[idx],
        pointBackgroundColor: DATASET_COLOR_IDENTIFIERS[idx],
        pointRadius: selected ? 0 : 5,
        datalabels: {
          color: 'transparent'
        }
      };
    })
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(GapTooltips, {
    selected: selected,
    onLoad: onLoad,
    gapTooltips: gapTooltips,
    respondentLabel: data[1].label
  }), /*#__PURE__*/React.createElement(CustomPointLabels, {
    motivatorIconPositions: motivatorIconPositions,
    handleSetSelected: handleSetSelected,
    selected: selected,
    handleMixpanelEvent: handleMixpanelEvent
  }), /*#__PURE__*/React.createElement(Radar, {
    id: "myChart",
    ref: function ref(_ref3) {
      return chartRef = _ref3;
    },
    onClick: reset,
    plugins: [ChartDataLabels, {
      afterDraw: function afterDraw(_ref2) {
        var scales = _ref2.scales,
          ctx = _ref2.ctx;
        var pointLabels = scales.r._pointLabels;
        var yCenter = scales.r.chart.height / 2;

        // Find motivator gaps between two data sets
        var gaps = getGaps(scales.r.chart.$datalabels._datasets);
        handleSetMotivatorIconPositions(scales.r._pointLabelItems.reduce(function (acc, val, idx) {
          // Determine CustomPointLabel coordinates
          var left;
          var top;
          if (val.textAlign === 'left') {
            left = val.left;
          }
          if (val.textAlign === 'right') {
            left = val.right;
          }
          if (val.textAlign === 'center') {
            left = (val.left + val.right) / 2;
          }
          if (val.y > yCenter) {
            top = val.top;
          } else {
            top = val.bottom;
          }
          var gap = gaps.find(function (g) {
            return g.idx === idx;
          });

          // Includes CustomPointLabel coordinates, and other relevant data
          acc[pointLabels[idx]] = {
            scaleId: parseInt(Object.entries(SCALES_BY_ID).find(function (entry) {
              return entry[1] === pointLabels[idx].toLowerCase();
            })[0], 10),
            x: val.x,
            y: val.y,
            left: left,
            top: top,
            idx: idx,
            isSelectable: !!gap,
            metadata: {
              gap: gap ? {
                first: parseInt(gap.first._model.lines[0], 10),
                second: parseInt(gap.second._model.lines[0], 10),
                diff: Math.abs(parseInt(gap.first._model.lines[0], 10) - parseInt(gap.second._model.lines[0], 10))
              } : null
            }
          };
          return acc;
        }, {}));

        // draw gap
        for (var i = 0; i < gaps.length; i++) {
          var _selectedRef$current;
          var gap = gaps[i];
          if (gap.idx === ((_selectedRef$current = selectedRef.current) === null || _selectedRef$current === void 0 ? void 0 : _selectedRef$current.idx)) {
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(gap.first._el.x, gap.first._el.y);
            ctx.lineTo(gap.second._el.x, gap.second._el.y);
            ctx.lineWidth = 5;
            ctx.strokeStyle = MOTIVATOR_COLORS["".concat(pointLabels[gap.idx].toUpperCase().replace(' ', '_'), "_PRIMARY")];
            ctx.stroke();

            // Draw highlighted data nodes
            ctx.beginPath();
            ctx.arc(gap.first._el.x, gap.first._el.y, 6, 6, 6 * Math.PI);
            ctx.fillStyle = LIGHT_BLUE;
            ctx.fill();
            ctx.restore();
            ctx.beginPath();
            ctx.arc(gap.second._el.x, gap.second._el.y, 6, 6, 6 * Math.PI);
            ctx.fillStyle = LIGHT_RED;
            ctx.fill();
            ctx.restore();
            handleSetTooltips({
              midpoint: getMidPoint({
                x: gap.first._el.x,
                y: gap.first._el.y
              }, {
                x: gap.second._el.x,
                y: gap.second._el.y
              }),
              first: {
                x: gap.first._el.x,
                y: gap.first._el.y
              },
              second: {
                x: gap.second._el.x,
                y: gap.second._el.y
              }
            });
          }
        }
      }
    }],
    data: chartData,
    height: "490px",
    width: "490px",
    options: {
      responsive: responsiveOption,
      plugins: {
        legend: {
          display: false
        }
      },
      onHover: function onHover(e) {
        var chart = e.chart;
        var tooltip = chart === null || chart === void 0 ? void 0 : chart.tooltip;
        if (!tooltip) {
          return;
        }
        if (tooltip && tooltip.getActiveElements().length > 0) {
          handleMixpanelEvent('gap_hovers_on_chart_dots');
          setPointTooltipHovered(true);
        } else {
          setPointTooltipHovered(false);
        }
      },
      scales: {
        r: {
          angleLines: {
            color: FADED_MID_BLUE
          },
          ticks: {
            beginAtZero: true,
            backdropColor: 'transparent',
            color: theme.palette.font.primary,
            font: {
              size: 11
            },
            z: 1,
            callback: function callback(v) {
              return getTickLabels(v, t);
            }
          },
          min: 0,
          grid: {
            circular: true,
            color: function color(v) {
              return getTickLineColor(v.index);
            }
          },
          pointLabels: {
            color: function color() {
              return 'transparent';
            },
            callback: function callback(_, index) {
              var motivator = SCALES_BY_ID[index + 1];
              return motivator.toUpperCase();
            }
          }
        }
      }
    }
  }));
};
RadarChartJS.propTypes = {
  data: PropTypes.array.isRequired,
  onSelectMotivator: PropTypes.func
};
export default RadarChartJS;